import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@material-ui/core';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../../redux/actions/alertActions';

// Server
import { listRooms } from '../../../api';

// Icons
import RefreshIcon from '@material-ui/icons/Refresh';

// Components
import RoomListCard from '../list/RoomListCard';
import FilterWidget from '../../../components/FilterWidget';
import { SearchField, ListNote, Pager, paginate, sameRows } from '../../../components/ListControls';

// Constants
import { FilterOptions } from '../../../constants';
import { T } from '../../../constants/tokens';

/** How often the list re-reads room statuses, matching the map. */
const REFRESH_MS = 5000;

/**
 * Every room in the school, under the live map.
 *
 * It lists all rooms rather than the floor on screen, so it still answers
 * "which rooms are not normal" across buildings. Choosing one hands it to the
 * map, which switches building and floor if needed and selects it there.
 */
function RoomList({ selectedRoomID, onSelect, setError }) {
  const [rooms, setRooms] = useState(null);
  const [filtered, setFiltered] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const mounted = useRef(true);
  const lastFiltered = useRef(null);

  const load = useCallback(() => {
    listRooms()
      .then((result) => {
        if (mounted.current) setRooms(result);
      })
      .catch((error) => {
        if (mounted.current) setError(error ? error : 'Could not retrieve the rooms.');
      });
  }, [setError]);

  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load]);

  const source = filtered ?? rooms;
  const needle = search.trim().toLowerCase();
  const visible = source == null
    ? null
    : source.filter((room) => !needle || (room.getIdentifier() ?? '').toLowerCase().includes(needle));
  const { page: current, pageCount, pageRows } = paginate(visible ?? [], page);
  const selected = rooms?.find((room) => room.getID() === selectedRoomID) ?? null;

  return (
    <section style={{ marginTop: 48 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 32, fontWeight: 600, lineHeight: '40px', letterSpacing: '-1.28px', color: T.ink }}>
          Rooms
        </h2>
        <span style={{ fontSize: 14, lineHeight: '20px', color: T.body }}>
          {visible == null ? 'Loading…' : (visible.length === 1 ? '1 room' : visible.length + ' rooms')}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <SearchField
          value={search}
          label='search rooms'
          placeholder='Search rooms'
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <FilterWidget
          filterOptions={FilterOptions.ROOM_LIST}
          data={rooms ?? []}
          updateFilteredData={(data) => {
            // Back to page 1 when the filter changes what is listed, not on
            // every refresh that re-runs it.
            if (!sameRows(lastFiltered.current, data, (room) => room.getID())) setPage(1);
            lastFiltered.current = data;
            setFiltered(data);
          }}
        />
        <IconButton aria-label='Refresh rooms' onClick={load} style={{ background: T.elevated, color: T.ink, flex: 'none' }}>
          <RefreshIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {visible == null && <ListNote>Loading…</ListNote>}
      {visible != null && visible.length === 0 &&
        <ListNote>{rooms?.length ? 'Nothing matches the current search and filters.' : 'No rooms configured yet.'}</ListNote>}
      {pageRows.map((room) => (
        <RoomListCard
          key={room.getID()}
          room={room}
          selectedCardObject={selected}
          onClick={() => onSelect(room)}
        />
      ))}

      <Pager page={current} pageCount={pageCount} onChange={setPage} />
    </section>
  );
}

RoomList.propTypes = {
  selectedRoomID: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired
};

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(null, mapDispatchToProps)(RoomList);
